export type MeasurementUnit = "mm" | "in";

const MILLIMETERS_PER_INCH = 25.4;
const DISPLAY_DECIMALS: Record<MeasurementUnit, number> = { in: 4, mm: 4 };
const STORAGE_DECIMALS = 4;

export function normalizeDecimal(
  value: string | number,
  decimals = STORAGE_DECIMALS,
): string {
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric)) throw new Error("Invalid measurement");
  return numeric
    .toFixed(decimals)
    .replace(/(\.\d*?[1-9])0+$/, "$1")
    .replace(/\.0+$/, "");
}

export function fromMillimeters(
  value: string | number | null | undefined,
  unit: MeasurementUnit,
): string | null {
  if (value == null) return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return normalizeDecimal(
    unit === "in" ? numeric / MILLIMETERS_PER_INCH : numeric,
    DISPLAY_DECIMALS[unit],
  );
}

export function toMillimeters(
  value: string | number,
  unit: MeasurementUnit,
): string {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) throw new Error("Invalid measurement");
  return normalizeDecimal(
    unit === "in" ? numeric * MILLIMETERS_PER_INCH : numeric,
    STORAGE_DECIMALS,
  );
}

export function toCanonicalMillimeters(
  displayedValue: string | number,
  unit: MeasurementUnit,
  preservedMillimeters?: string,
): string {
  return preservedMillimeters ?? toMillimeters(displayedValue, unit);
}

export type PreservedMeasurements = Partial<
  Record<"bladeLength" | "bladeWidth" | "handleLength", string>
>;

export function clearPreservedMeasurement(
  preserved: PreservedMeasurements,
  field: keyof PreservedMeasurements,
): PreservedMeasurements {
  const next = { ...preserved };
  delete next[field];
  return next;
}

export function convertMeasurement(
  value: string | number,
  from: MeasurementUnit,
  to: MeasurementUnit,
): string {
  if (from === to) return normalizeDecimal(value, DISPLAY_DECIMALS[to]);
  return (
    fromMillimeters(toMillimeters(value, from), to) ??
    normalizeDecimal(value, DISPLAY_DECIMALS[to])
  );
}

export function roundMeasurement(
  value: string | number,
  unit: MeasurementUnit,
): string {
  return normalizeDecimal(value, DISPLAY_DECIMALS[unit]);
}

export function formatMeasurement(
  valueMm: string | number | null | undefined,
  unit: MeasurementUnit,
): string | null {
  const value = fromMillimeters(valueMm, unit);
  return value == null ? null : `${roundMeasurement(value, unit)} ${unit}`;
}
